import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { clientConversations, clients, escalations } from '../../api/client'
import PageHeader from '../../components/PageHeader'

function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } else if (diffDays === 1) {
    return 'Yesterday'
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' })
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function ConversationCard({ conversation }) {
  const client = conversation.client
  const initials = `${client?.firstName?.[0] || ''}${client?.lastName?.[0] || ''}`.toUpperCase()
  const unread = conversation.unreadCount || 0

  return (
    <Link
      to={`/client-chats/${conversation.id}`}
      className="flex items-start gap-3 p-4 bg-white dark:bg-dial-surface rounded-xl shadow-sm active:bg-gray-50 dark:active:bg-dial-elevated dark:border dark:border-dial-border"
    >
      <div className="relative flex-shrink-0">
        <div className="w-12 h-12 bg-primary-100 dark:bg-dial-elevated text-primary-600 dark:text-lume-teal rounded-full flex items-center justify-center font-semibold">
          {initials || '?'}
        </div>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 flex items-center justify-center text-[11px] font-bold bg-lume-teal text-dial-black rounded-full">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className={`truncate ${unread > 0 ? 'font-bold text-gray-900 dark:text-indices-bright' : 'font-medium text-gray-900 dark:text-indices-bright'}`}>
            {client?.firstName} {client?.lastName}
          </p>
          <span className="text-xs text-gray-400 dark:text-indices-muted flex-shrink-0">
            {formatDate(conversation.updatedAt)}
          </span>
        </div>
        <p className="text-sm text-primary-600 dark:text-lume-teal/70">
          {conversation.conversationTypeDisplay || conversation.conversationType}
        </p>
        {conversation.lastMessage && (
          <p className={`text-sm truncate mt-1 ${unread > 0 ? 'font-medium text-gray-700 dark:text-indices-bright' : 'text-gray-500 dark:text-indices-medium'}`}>
            {conversation.lastMessage.role === 'user' ? '' : 'AI: '}
            {conversation.lastMessage.preview}
          </p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-gray-400 dark:text-indices-muted">
            {conversation.messageCount} messages
          </span>
          {!conversation.isActive && (
            <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-dial-elevated text-gray-500 dark:text-indices-muted rounded-full">
              Archived
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}

export default function ClientChatsPage() {
  const [searchParams] = useSearchParams()
  const clientIdFilter = searchParams.get('clientId')
  const [search, setSearch] = useState('')
  const queryClient = useQueryClient()

  const handleDismissEscalation = async (e, id) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await escalations.resolve(id, 'Dismissed by trainer')
      queryClient.invalidateQueries({ queryKey: ['escalations'] })
    } catch (err) {
      console.error('Failed to dismiss escalation:', err)
    }
  }

  const { data: conversationList, isLoading } = useQuery({
    queryKey: ['clientConversations', clientIdFilter],
    queryFn: () => clientConversations.list(clientIdFilter),
  })

  const { data: clientList } = useQuery({
    queryKey: ['clients'],
    queryFn: clients.list,
  })

  const { data: escalationData } = useQuery({
    queryKey: ['escalations'],
    queryFn: () => escalations.list(),
  })

  const unreadEscalations = escalationData?.escalations || []

  // Filter conversations by search term
  const filteredConversations = conversationList?.filter(conv => {
    if (!search) return true
    const searchLower = search.toLowerCase()
    const client = conv.client
    return (
      client?.firstName?.toLowerCase().includes(searchLower) ||
      client?.lastName?.toLowerCase().includes(searchLower) ||
      conv.lastMessage?.preview?.toLowerCase().includes(searchLower)
    )
  }) || []

  // Get client name for filtered view
  const clientArray = Array.isArray(clientList) ? clientList : clientList?.Client || []
  const filteredClient = clientIdFilter && clientArray.find(c => String(c.id) === clientIdFilter)

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        title={filteredClient ? `${filteredClient.firstName}'s Chats` : 'Client Chats'}
        showBack
        backTo={filteredClient ? `/clients/${clientIdFilter}` : '/'}
      />

      <div className="px-4 py-4 space-y-4">
        {/* Escalation Alerts */}
        {unreadEscalations.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-indices-muted">
              Escalations ({unreadEscalations.length})
            </h3>
            {unreadEscalations.map(esc => (
              <Link
                key={esc.id}
                to={`/client-chats/${esc.conversationId}`}
                className={`block p-3 rounded-xl border ${
                  esc.severity === 'urgent'
                    ? 'bg-red-50 dark:bg-status-error/10 border-red-200 dark:border-status-error/30'
                    : esc.severity === 'attention'
                    ? 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30'
                    : 'bg-blue-50 dark:bg-status-info/10 border-blue-200 dark:border-status-info/30'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className={`mt-0.5 flex-shrink-0 w-2 h-2 rounded-full ${
                    esc.severity === 'urgent' ? 'bg-red-500 dark:bg-status-error' :
                    esc.severity === 'attention' ? 'bg-amber-500 dark:bg-amber-400' :
                    'bg-blue-500 dark:bg-status-info'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-indices-bright">
                        {esc.clientName}
                      </span>
                      <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                        esc.severity === 'urgent'
                          ? 'bg-red-100 dark:bg-status-error/20 text-red-700 dark:text-status-error'
                          : esc.severity === 'attention'
                          ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400'
                          : 'bg-blue-100 dark:bg-status-info/20 text-blue-700 dark:text-status-info'
                      }`}>
                        {esc.escalationType?.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-indices-medium mt-0.5 line-clamp-2">
                      {esc.summary}
                    </p>
                  </div>
                  <button
                    onClick={(e) => handleDismissEscalation(e, esc.id)}
                    className="flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-200 dark:text-indices-muted dark:hover:text-indices-bright dark:hover:bg-dial-elevated"
                    title="Dismiss"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search conversations..."
            className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>

        {/* Clear filter link */}
        {filteredClient && (
          <Link
            to="/client-chats"
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            View all client chats
          </Link>
        )}

        {/* Conversations List */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse bg-white rounded-xl p-4 h-24" />
            ))}
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-gray-500">No client conversations yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Client AI chat history will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredConversations.map(conversation => (
              <ConversationCard key={conversation.id} conversation={conversation} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
